import PropTypes from 'prop-types';
import React from 'react';
import { injectIntl } from 'react-intl';
import {
  Button, Card, CardBody, CardFooter, CardHeader,
  Form, FormFeedback, FormGroup, FormText, Input, Label,
} from 'reactstrap';
import isEmail from 'validator/es/lib/isEmail';

import FlashesStore from 'src/stores/FlashesStore';

/**
 * AdminUserInviteAdminForm component.
 *
 * This component provides a form for inviting a new admin user to the Enosi platform.
 * It collects email, given name, and family name information for the new admin.
 * @param {object} props - Component props
 * @param {Function} props.handleInvite - Function to handle the invitation submission
 * @param {boolean} props.processing - Whether the form submission is being processed
 * @returns {React.ReactComponentElement} - Form component.
 */
class AdminUserInviteAdminForm extends React.Component {
  static mutationName = 'inviteAdminUser';

  constructor(props) {
    super(props);

    this.state = {
      email: '',
      givenName: '',
      familyName: '',
      emailValid: null,
    };
  }

  isValid = () => {
    const { emailValid } = this.state;

    return emailValid !== null && emailValid;
  };

  handleSubmit = (event) => {
    event.preventDefault();

    const { handleInvite, processing, intl } = this.props;

    if (processing) {
      const msgInfo = intl.formatMessage({
        id: 'admin.admin_user.admin_user_invite_admin.form.state.invalid_still_processing',
        defaultMessage: 'We are still processing your request...',
      });
      FlashesStore.flash(FlashesStore.INFO, msgInfo, AdminUserInviteAdminForm.mutationName);
      return;
    }

    if (!this.isValid()) {
      const msgInvalidData = intl.formatMessage({
        id: 'admin.admin_user.admin_user_invite_admin.form.state.invalid_data',
        defaultMessage: 'Form data not valid. Please see below.',
      });
      FlashesStore.flash(FlashesStore.ERROR, msgInvalidData, AdminUserInviteAdminForm.mutationName);
      return;
    }

    const {
      email, familyName, givenName,
    } = this.state;

    const input = {
      email, familyName, givenName,
    };

    handleInvite(input);
  };

  handleEmailChange = (event) => {
    const { value: email } = event.target;
    const emailValid = isEmail(email);

    this.setState({ email, emailValid });
  };

  handleFamilyNameChange = (event) => {
    const { value } = event.target;

    this.setState({ familyName: value });
  };

  handleGivenNameChange = (event) => {
    const { value } = event.target;

    this.setState({ givenName: value });
  };

  render() {
    const { intl, processing } = this.props;

    const {
      email, familyName, givenName, emailValid,
    } = this.state;

    // Page and form titles
    const formHeader = intl.formatMessage({
      id: 'admin.admin_user.admin_user_invite_admin.form.header',
      defaultMessage: 'Invite a new admin to the Enosi platform',
    });

    // Email field labels and messages
    const emailLabel = intl.formatMessage({
      id: 'admin.admin_user.admin_user_invite_admin.form.email.label',
      defaultMessage: 'Email*',
    });
    const emailFeedbackInvalid = intl.formatMessage({
      id: 'admin.admin_user.admin_user_invite_admin.form.email.feedback.invalid',
      defaultMessage: 'Not a valid email',
    });
    const emailHelpText = intl.formatMessage({
      id: 'admin.admin_user.admin_user_invite_admin.form.email.help_text',
      defaultMessage: 'This email address will be used by the admin user to log onto the Enosi platform',
    });

    // Given name field label
    const givenNameLabel = intl.formatMessage({
      id: 'admin.admin_user.admin_user_invite_admin.form.given_name.label',
      defaultMessage: 'Given Name',
    });

    // Family name field label
    const familyNameLabel = intl.formatMessage({
      id: 'admin.admin_user.admin_user_invite_admin.form.family_name.label',
      defaultMessage: 'Family Name',
    });

    // Submit button label
    const submitLabel = intl.formatMessage({
      id: 'admin.admin_user.admin_user_invite_admin.form.submit.label',
      defaultMessage: 'Send invitation',
    });

    return (
      <Form onSubmit={this.handleSubmit} disabled={processing}>
        <Card>
          <CardHeader tag="h2">
            {formHeader}
          </CardHeader>
          <CardBody>
            <FormGroup>
              <Label for="email">{emailLabel}</Label>
              <Input
                type="email"
                name="email"
                id="email"
                value={email}
                onChange={this.handleEmailChange}
                disabled={processing}
                valid={emailValid}
                invalid={emailValid !== null && !emailValid}
              />
              <FormFeedback>{emailFeedbackInvalid}</FormFeedback>
              <FormText>
                {emailHelpText}
              </FormText>
            </FormGroup>
            <FormGroup>
              <Label for="givenName">{givenNameLabel}</Label>
              <Input
                type="text"
                name="givenName"
                id="givenName"
                value={givenName}
                onChange={this.handleGivenNameChange}
                disabled={processing}
              />
            </FormGroup>
            <FormGroup>
              <Label for="familyName">{familyNameLabel}</Label>
              <Input
                type="text"
                name="familyName"
                id="familyName"
                value={familyName}
                onChange={this.handleFamilyNameChange}
                disabled={processing}
              />
            </FormGroup>
          </CardBody>
          <CardFooter>
            <Button disabled={processing}>{submitLabel}</Button>
          </CardFooter>
        </Card>
      </Form>
    );
  }
}

AdminUserInviteAdminForm.propTypes = {
  handleInvite: PropTypes.func,
  processing: PropTypes.bool,
  intl: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

AdminUserInviteAdminForm.defaultProps = {
  handleInvite: null,
  processing: false,
};

export default injectIntl(AdminUserInviteAdminForm);
